// File: src/components/admin/plans/PlanHistoryList.js
// AC20: per-plan audit log render.
//
// Lazy-loads when expanded (the editor renders this inside an Accordion).

import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Table, TableBody, TableCell, TableHead, TableRow,
    Chip, CircularProgress,
} from '@mui/material';
import { mainApi } from '../../../services/mainApi';

function formatTs(ts) {
    if (!ts) return '';
    try {
        const d = new Date(ts);
        return d.toLocaleString();
    } catch {
        return String(ts);
    }
}

function formatValue(v) {
    if (v === null || v === undefined) return '—';
    if (typeof v === 'boolean') return v ? 'true' : 'false';
    if (Array.isArray(v)) return v.length === 0 ? '[]' : `[${v.join(', ')}]`;
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
}

export default function PlanHistoryList({ planId, refreshKey }) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [history, setHistory] = useState([]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        mainApi.getPlanHistory(planId)
            .then((data) => { if (!cancelled) setHistory(data.history || []); })
            .catch((e) => { if (!cancelled) setError(e?.response?.data?.error || e.message); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [planId, refreshKey]);

    if (loading) {
        return <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>;
    }
    if (error) {
        return <Typography color="error" variant="body2">{error}</Typography>;
    }
    if (history.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" data-testid={`plan-history-empty-${planId}`}>
                No changes recorded yet.
            </Typography>
        );
    }

    return (
        <Box data-testid={`plan-history-${planId}`}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 600, width: 180 }}>When</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Actor</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Changes</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {history.map((h, i) => (
                        <TableRow key={i}>
                            <TableCell sx={{ color: 'text.secondary' }}>{formatTs(h.ts)}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{h.actor}</TableCell>
                            <TableCell>
                                <Chip
                                    size="small"
                                    label={h.action}
                                    color={h.action === 'archive' ? 'warning'
                                        : h.action === 'create' ? 'success' : 'default'}
                                />
                            </TableCell>
                            <TableCell>
                                {Array.isArray(h.diff) && h.diff.length > 0 ? (
                                    h.diff.map((d, j) => (
                                        <Box key={j} sx={{ fontSize: '0.8rem', mb: 0.25 }}>
                                            <code>{d.field}</code>: <span style={{ color: '#888' }}>{formatValue(d.from)}</span>
                                            {' → '}
                                            <b>{formatValue(d.to)}</b>
                                        </Box>
                                    ))
                                ) : (
                                    <Typography variant="caption" color="text.secondary">(no diff)</Typography>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Box>
    );
}
